import { type VariantProps, cva } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { ButtonContent } from "@/components/ui/buttons/shared/ButtonContent";
import { uiOutlineClass } from "@/components/ui/global/outline";
import { TooltipEllipsis } from "@/components/ui/overlays/Tooltip/TooltipEllipsis";

export const button = cva(
  ["flex items-center justify-center gap-icon-to-label-spacing rounded-button-rounding", uiOutlineClass],
  {
    variants: {
      width: {
        s: "w-full max-w-4-5xs",
        m: "w-full max-w-3-5xs",
        l: "w-full max-w-3xs",
        fit: "w-full",
        auto: "w-auto",
      },
      size: {
        small: "p-height-s",
        default: "p-height-m",
        large: "p-height-l",
      },
      iconPosition: {
        left: "flex-row",
        right: "flex-row-reverse",
      },
      variant: {
        primary: [
          "bg-interactive-primary-idle",
          "text-interactive-primary-on",
          "hover:bg-interactive-primary-hover",
          "active:bg-interactive-primary-pressed",
          "focus-visible:outline-interactive-primary-focus",
          "disabled:bg-interactive-primary-disabled",
        ],
        secondary: [
          "bg-elevation-surface-1",
          "disabled:bg-interactive-secondary-disabled",
          "border border-elevation-outline-2",
          "hover:border-elevation-outline-3",
          "focus:border-elevation-outline-3",
          "text-interactive-primary-pressed",
        ],
        outlined: [
          "border",
          "border-interactive-outlined-idle",
          "text-interactive-outlined-on",
          "hover:border-interactive-outlined-hover",
          "hover:text-interactive-outlined-hover",
          "active:border-interactive-outlined-pressed",
          "active:text-interactive-outlined-pressed",
          "focus-visible:outline-interactive-outlined-focus",
          "disabled:border-interactive-outlined-disabled",
          "disabled:text-interactive-outlined-disabled",

          // styles for ToggleButton - we include them here to avoid code duplication
          "selected:bg-interactive-outlined-toggled",
          "selected:text-interactive-outlined-on-toggled",
          "selected:hover:bg-interactive-outlined-hover",
          "selected:hover:text-interactive-outlined-on-toggled",
          "selected:disabled:bg-interactive-outlined-disabled",
        ],
        signup: [
          "!bg-[#FBFBFB]",
          "text-text-inverted-primary",
          "border",
          "border-text-inverted-primary",
          "border-interactive-outlined-idle",
          "hover:border-text-inverted-primary",
          "hover:text-text-inverted-primary",
          "active:border-text-inverted-primary",
          "active:text-text-inverted-primary",
          "focus-visible:outline-interactive-outlined-focus",
          "disabled:border-text-inverted-primary",
          "disabled:text-text-inverted-primary",
        ],
        inverse: [
          "bg-elevation-background",
          "text-interactive-primary-idle",
          "hover:bg-elevation-background",
          "active:bg-elevation-background",
          "focus-visible:outline-elevation-background",
        ],
        text: [
          "border-none",
          "text-interactive-outlined-on",
          "hover:text-interactive-outlined-hover",
          "active:text-interactive-outlined-pressed",
          "focus-visible:outline-interactive-outlined-focus",
          "disabled:text-interactive-outlined-disabled",
        ],
        filter: [
          "rounded-xs bg-elevation-surface-2",
          "text-text-inverted-tertiary",
          "hover:bg-elevation-surface-2",
          "active:bg-elevation-surface-2",
          "focus-visible:outline-elevation-surface-2",
        ],
        tertiary: [
          "!bg-elevation-surface-1 rounded-xs",
          "text-text-inverted-tertiary",
          "border",
          "border-gradient",
          "shadow-1",
          "hover:bg-elevation-surface-1",
          "active:bg-elevation-surface-1",
          "focus-visible:outline-elevation-surface-1",
          "disabled:text-interactive-primary-disabled",
        ],
      },
    },
    defaultVariants: {
      width: "fit",
      size: "default",
      variant: "primary",
      iconPosition: "left",
    },
  },
);

type ButtonVariantProps = VariantProps<typeof button>;

type ButtonProps = ButtonVariantProps &
  AriaButtonProps & {
    children?: string | React.ReactNode;
    isLoading?: boolean;
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    link?: LinkProps & { target?: string };
    hideText?: boolean;
    noDisableWhenLoading?: boolean;
    iconClassName?: string;
    labelClassName?: string;
    textVariant?: "prominent-2" | "default";
    labelSize?: "label-2" | "body-3" | "body-4" | "label-3" | "label-4" | "label-5" | "label-6";
  };

export const Button = ({
  width,
  size,
  variant,
  icon: Icon,
  iconPosition,
  children,
  isLoading,
  className,
  link,
  hideText,
  noDisableWhenLoading,
  iconClassName = "h-6 w-6",
  labelClassName,
  labelSize = "label-2",
  ...props
}: ButtonProps) => {
  const Component: any = link ? Link : AriaButton;

  return (
    <TooltipEllipsis text={typeof children === "string" ? children : ""}>
      {(onContentRef) => (
        <Component
          {...props}
          {...link}
          isDisabled={props.isDisabled || (isLoading && !noDisableWhenLoading)}
          className={button({ width, size, variant, iconPosition, className })}
        >
          <ButtonContent
            iconClassName={iconClassName}
            className={labelClassName}
            isLoading={isLoading}
            icon={Icon}
            text={typeof children === "string" ? children : ""}
            ref={onContentRef}
            hideText={hideText}
            variant={variant === "filter" ? "default" : "prominent-2"}
            size={variant === "filter" ? "body-3" : labelSize}
          />
          {typeof children !== "string" && children}
        </Component>
      )}
    </TooltipEllipsis>
  );
};
